import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Search, 
  Filter, 
  Download, 
  RefreshCw, 
  X,
  Users,
  FolderKanban,
  Tag,
  Hash,
  ChevronDown,
  ChevronUp,
  Calendar,
  DollarSign,
  Briefcase,
  ArrowRightLeft,
  CheckSquare,
  Square,
  CheckCircle,
  Zap,
  PlayCircle,
  Loader2,
  Check,
  Play,
  Pause,
  AlertTriangle,
  FileText
} from 'lucide-react';
import { toast } from 'react-toastify';

const MigrationPage = () => {
  // Estados dos filtros
  const [filters, setFilters] = useState({
    pipeline: '',
    stage: '',
    dealId: '',
    assignedByName: ''
  });

  // Estados dos resultados
  const [deals, setDeals] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedDeal, setExpandedDeal] = useState(null);
  
  // Estados da migração
  const [migrationConfig, setMigrationConfig] = useState({
    targetPipeline: '',
    targetStage: '',
    targetUserId: '',
    targetAdvID: ''
    
  });
  const [migrating, setMigrating] = useState(false);
  
  // Estados de seleção múltipla
  const [selectedDeals, setSelectedDeals] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  // Estados da migração automática de fase
  const [autoMigration, setAutoMigration] = useState({
    sourcePipeline: '',
    sourceStage: '',
    targetPipeline: '',
    targetStage: '',
    migrateFields: true,    // Migrar/substituir todos os campos do card criado
    migrateComments: true, // Migrar comentários
    migrateFiles: true     // Migrar arquivos dos comentários
  });
  const [autoMigrating, setAutoMigrating] = useState(false);
  const [autoMigrationProgress, setAutoMigrationProgress] = useState({
    total: 0,
    current: 0,
    success: 0,
    errors: 0,
    currentDeal: ''
  });
  const [showMigrationModal, setShowMigrationModal] = useState(false);
  const [migrationPaused, setMigrationPaused] = useState(false);
  const [migrationCancelled, setMigrationCancelled] = useState(false);

  // Estados para migração de comentários e anexos
  const [commentsConfig, setCommentsConfig] = useState({
    sourceDealId: '',      // ID do negócio no Bitrix antigo
    targetDealId: '',      // ID do card no Bitrix novo (opcional)
    targetPipeline: '',    // Pipeline do Bitrix novo (se não fornecer targetDealId)
    targetStage: '',       // Estágio do pipeline (obrigatório se criar novo deal)
    replaceAll: false,     // Substituir todos os campos do card existente
    migrateFields: true,   // Migrar campos do card
    migrateComments: true, // Migrar comentários
    migrateFiles: true     // Migrar arquivos dos comentários
  });

  // Estados para migração entre cards existentes
  const [cardToCardConfig, setCardToCardConfig] = useState({
    sourceDealId: '',      // ID do card de origem (Bitrix antigo)
    targetDealId: '',      // ID do card de destino (Bitrix novo existente)
    migrateFields: true,   // Migrar todos os campos
    migrateComments: true,  // Migrar comentários
    migrateFiles: true     // Migrar arquivos dos comentários
  });
  const [cardToCardLoading, setCardToCardLoading] = useState(false);
  const [cardToCardResult, setCardToCardResult] = useState(null);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentsProgress, setCommentsProgress] = useState({
    total: 0,
    current: 0,
    success: 0,
    errors: 0,
    currentDeal: ''
  });
  const [commentsMigrationResult, setCommentsMigrationResult] = useState(null); // Resultado da migração
  const [commentsMigrating, setCommentsMigrating] = useState(false);
  const [commentsErrors, setCommentsErrors] = useState([]); // Array para armazenar erros
  const [showCommentsErrors, setShowCommentsErrors] = useState(false); // Controlar visibilidade dos erros

  // Estatísticas
  const [stats, setStats] = useState({
    total: 0,
    totalValue: 0,
    avgValue: 0,
    byPipeline: {}
  });

  // Carregar usuários ao montar o componente
  useEffect(() => {
    loadUsers();
  }, []);

  // Função para carregar usuários
  const loadUsers = async () => {
    try {
      const response = await fetch('https://backendconsultabitrix.villelaintegra.com.br/api/bitrix/users');
      const data = await response.json();
      
      if (data.success) {
        setUsers(data.users || []);
      }
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
    }
  };

  // Função para buscar deals
  const searchDeals = async () => {
    // Verificar se pelo menos um filtro foi preenchido
    const hasFilters = Object.values(filters).some(value => value !== '');
    
    if (!hasFilters) {
      toast.warning('Preencha pelo menos um filtro para buscar');
      return;
    }
    
    setLoading(true);
    
    try {
      // Construir query params
      const params = new URLSearchParams();
      
      if (filters.pipeline) params.append('pipeline', filters.pipeline);
      if (filters.stage) params.append('stage', filters.stage);
      if (filters.dealId) params.append('dealId', filters.dealId);
      if (filters.assignedByName) params.append('assignedByName', filters.assignedByName);

      const response = await fetch(`https://backendconsultabitrix.villelaintegra.com.br/api/bitrix/deals?${params.toString()}`);
      const data = await response.json();

      if (data.success) {
        setDeals(data.deals || []);
        calculateStats(data.deals || []);
        if ((data.deals?.length || 0) === 0) {
          toast.warning('Nenhum negócio encontrado. Confira o ID da pipeline (ex: 41) e o código do estágio (ex: C41:EXECUTING, não um número).');
        } else {
          toast.success(`${data.deals.length} negócios encontrados!`);
        }
      } else {
        setDeals([]);
        setStats({ total: 0, totalValue: 0, avgValue: 0, byPipeline: {} });
        const authMsg = data.code === 'authorization_error'
          ? 'Conexão com o Bitrix antigo falhou: webhook expirado. Peça para atualizar BITRIX_WEBHOOK_OLD no servidor.'
          : (data.message || 'Erro ao buscar negócios');
        toast.error(authMsg);
      }
    } catch (error) {
      console.error('Erro ao buscar deals:', error);
      toast.error('Erro ao buscar negócios. Verifique a conexão.');
    } finally {
      setLoading(false);
    }
  };

  // Calcular estatísticas
  const calculateStats = (dealsData) => {
    const total = dealsData.length;
    const totalValue = dealsData.reduce((sum, deal) => sum + parseFloat(deal.OPPORTUNITY || 0), 0);
    const avgValue = total > 0 ? totalValue / total : 0;

    const byPipeline = {};
    dealsData.forEach(deal => {
      const pipeline = deal.CATEGORY_ID || 'Sem Pipeline';
      byPipeline[pipeline] = (byPipeline[pipeline] || 0) + 1;
    });

    setStats({ total, totalValue, avgValue, byPipeline });
  };

  // Limpar filtros
  const clearFilters = () => {
    setFilters({
      pipeline: '',
      stage: '',
      dealId: '',
      assignedByName: '',
      minDate: '',
      maxDate: ''
    });
    setDeals([]);
    setStats({ total: 0, totalValue: 0, avgValue: 0, byPipeline: {} });
  };

  // Exportar para CSV
  const exportToCSV = () => {
    if (deals.length === 0) {
      toast.warning('Nenhum negócio para exportar');
      return;
    }

    const headers = ['ID', 'Título', 'Pipeline', 'Estágio', 'Valor', 'Data Criação', 'Responsável', ""];
    const csvData = deals.map(deal => [
      deal.ID,
      `"${deal.TITLE || ''}"`,
      deal.CATEGORY_ID || '',
      deal.STAGE_ID || '',
      deal.OPPORTUNITY || '0',
      deal.DATE_CREATE || '',
      deal.ASSIGNED_BY_ID || ''
    ]);

    const csv = [
      headers.join(','),
      ...csvData.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `deals_migracao_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();

    toast.success('Arquivo CSV exportado!');
  };


  // Migrar negócio individual
  const migrateSingleDeal = async (dealId) => {
    if (!migrationConfig.targetPipeline || !migrationConfig.targetStage) {
      toast.warning('Configure a pipeline e estágio de destino primeiro');
      return;
    }

    setMigrating(true);

    try {
        const response = await fetch('https://backendconsultabitrix.villelaintegra.com.br/api/bitrix/migrate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          dealId: dealId,
          targetPipeline: migrationConfig.targetPipeline,
          targetStage: migrationConfig.targetStage,
          targetUserId: migrationConfig.targetUserId || null,
          targetAdvID: migrationConfig.targetAdvID || null
        })
      });

      const data = await response.json();

      if (data.success) {
        toast.success(`Negócio migrado com sucesso! Novo ID: ${data.newDealId}`);
        
        // Remover da lista
        setDeals(prev => prev.filter(deal => deal.ID !== dealId));
        
        // Remover da seleção se estava selecionado
        setSelectedDeals(prev => prev.filter(id => id !== dealId));
        
        // Atualizar estatísticas
        calculateStats(deals.filter(deal => deal.ID !== dealId));
        
      } else {
        toast.error(data.message || 'Erro na migração');
      }
    } catch (error) {
      console.error('Erro ao migrar negócio:', error);
      toast.error('Erro ao migrar negócio. Verifique a conexão.');
    } finally {
      setMigrating(false);
    }
  };

  // Migrar múltiplos negócios
  const migrateSelectedDeals = async () => {
    if (selectedDeals.length === 0) {
      toast.warning('Selecione pelo menos um negócio para migrar');
      return;
    }

    if (!migrationConfig.targetPipeline || !migrationConfig.targetStage) {
      toast.warning('Configure a pipeline e estágio de destino primeiro');
      return;
    }

    const confirmMsg = `Tem certeza que deseja migrar ${selectedDeals.length} negócio(s)?`;
    if (!window.confirm(confirmMsg)) {
      return;
    }

    setMigrating(true);

    try {
      toast.info(`Iniciando migração de ${selectedDeals.length} negócio(s)...`);

      const response = await fetch('https://backendconsultabitrix.villelaintegra.com.br/api/bitrix/migrate/batch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          dealIds: selectedDeals,
          targetPipeline: migrationConfig.targetPipeline,
          targetStage: migrationConfig.targetStage,
          targetUserId: migrationConfig.targetUserId || null
        })
      });

      const data = await response.json();

      if (data.success) {
        const { success, failed } = data.summary;
        
        if (failed === 0) {
          toast.success(`✅ ${success} negócio(s) migrado(s) com sucesso!`);
        } else {
          toast.warning(`⚠️ ${success} sucesso(s), ${failed} erro(s). Verifique os detalhes.`);
          
          // Mostrar erros
          if (data.errors && data.errors.length > 0) {
            data.errors.forEach(err => {
              toast.error(`Erro no negócio ${err.dealId}: ${err.error}`, { autoClose: 5000 });
            });
          }
        }
        
        // Remover negócios migrados com sucesso da lista
        const successIds = data.results.map(r => r.oldDealId);
        setDeals(prev => prev.filter(deal => !successIds.includes(deal.ID)));
        
        // Limpar seleção
        setSelectedDeals([]);
        setSelectAll(false);
        
        // Atualizar estatísticas
        calculateStats(deals.filter(deal => !successIds.includes(deal.ID)));
        
      } else {
        toast.error(data.message || 'Erro na migração em lote');
      }
    } catch (error) {
      console.error('Erro ao migrar negócios:', error);
      toast.error('Erro ao migrar negócios. Verifique a conexão.');
    } finally {
      setMigrating(false);
    }
  };

  // Toggle seleção de um negócio
  const toggleDealSelection = (dealId) => {
    setSelectedDeals(prev => {
      if (prev.includes(dealId)) {
        return prev.filter(id => id !== dealId);
      } else {
        return [...prev, dealId];
      }
    });
  };

  // Selecionar/Desselecionar todos
  const toggleSelectAll = () => {
    if (selectAll) {
      setSelectedDeals([]);
      setSelectAll(false);
    } else {
      setSelectedDeals(deals.map(deal => deal.ID));
      setSelectAll(true);
    }
  };

  // Migração automática de fase completa
  const migrateEntirePhase = async () => {
    if (!autoMigration.sourcePipeline || !autoMigration.sourceStage) {
      toast.warning('Preencha Pipeline e Estágio de origem');
      return;
    }

    if (!autoMigration.targetPipeline || !autoMigration.targetStage) {
      toast.warning('Preencha Pipeline e Estágio de destino');
      return;
    }

    // Mostrar modal de confirmação ao invés do alert feio
    setShowMigrationModal(true);
  };

  // Confirmar migração após modal
  const confirmMigration = async () => {
    setShowMigrationModal(false);
    setMigrationCancelled(false);
    setMigrationPaused(false);
    
    setAutoMigrating(true);
    setAutoMigrationProgress({ total: 0, current: 0, success: 0, errors: 0, currentDeal: '' });

    try {
      // Passo 1: Buscar TODOS os negócios da fase (com paginação)
      toast.info('📋 Buscando negócios da fase...');
      
      let allDealsFromPhase = [];
      let start = 0;
      const batchSize = 50;
      let hasMore = true;

      while (hasMore && !migrationCancelled) {
        // Verificar se está pausado
        while (migrationPaused && !migrationCancelled) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
        
        if (migrationCancelled) break;

        const params = new URLSearchParams();
        params.append('pipeline', autoMigration.sourcePipeline);
        params.append('stage', autoMigration.sourceStage);
        params.append('start', start.toString());

        const response = await fetch(
          `https://backendconsultabitrix.villelaintegra.com.br/api/bitrix/deals?${params.toString()}`
        );
        const data = await response.json();

        if (!data.success || !data.deals || data.deals.length === 0) {
          hasMore = false;
          break;
        }

        allDealsFromPhase = allDealsFromPhase.concat(data.deals);
        
        // Atualizar progresso da busca
        toast.info(`📦 Carregados ${allDealsFromPhase.length} negócios...`, { autoClose: 1000 });

        // Verificar se há mais páginas usando a resposta do backend
        if (data.hasMore && data.nextStart) {
          start = data.nextStart;
        } else {
          hasMore = false;
        }
      }

      if (migrationCancelled) {
        toast.info('Migração cancelada pelo usuário');
        return;
      }

      if (allDealsFromPhase.length === 0) {
        toast.warning('Nenhum negócio encontrado nesta fase');
        setAutoMigrating(false);
        return;
      }

      toast.success(`✅ ${allDealsFromPhase.length} negócios encontrados. Iniciando migração...`);

      // Inicializar progresso
      setAutoMigrationProgress({
        total: allDealsFromPhase.length,
        current: 0,
        success: 0,
        errors: 0,
        currentDeal: ''
      });

      // Passo 2: Migrar cada negócio
      const results = [];
      const errors = [];

      for (let i = 0; i < allDealsFromPhase.length; i++) {
        // Verificar se foi cancelado
        if (migrationCancelled) {
          toast.info('Migração cancelada pelo usuário');
          break;
        }

        // Verificar se está pausado
        while (migrationPaused && !migrationCancelled) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
        
        if (migrationCancelled) break;

        const deal = allDealsFromPhase[i];
        
        // Atualizar progresso
        setAutoMigrationProgress(prev => ({
          ...prev,
          current: i + 1,
          currentDeal: deal.TITLE || `ID: ${deal.ID}`
        }));

        try {
          const response = await fetch('https://backendconsultabitrix.villelaintegra.com.br/api/bitrix/migrate', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              dealId: deal.ID,
              targetPipeline: autoMigration.targetPipeline,
              targetStage: autoMigration.targetStage
            })
          });

          const data = await response.json();

          if (data.success) {
            results.push({
              oldDealId: deal.ID,
              newDealId: data.newDealId,
              title: deal.TITLE
            });
            
            setAutoMigrationProgress(prev => ({
              ...prev,
              success: prev.success + 1
            }));
          } else {
            errors.push({
              dealId: deal.ID,
              title: deal.TITLE,
              error: data.message || 'Erro desconhecido'
            });
            
            setAutoMigrationProgress(prev => ({
              ...prev,
              errors: prev.errors + 1
            }));
          }
        } catch (error) {
          errors.push({
            dealId: deal.ID,
            title: deal.TITLE,
            error: error.message
          });
          
          setAutoMigrationProgress(prev => ({
            ...prev,
            errors: prev.errors + 1
          }));
        }

        // Pequeno delay para não sobrecarregar
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      // Resultado final
      if (migrationCancelled) {
        toast.info('Migração cancelada pelo usuário');
      } else if (errors.length === 0) {
        toast.success(`🎉 Migração concluída! ${results.length} negócios migrados com sucesso!`, {
          autoClose: 10000
        });
      } else {
        toast.warning(
          `⚠️ Migração concluída: ${results.length} sucessos, ${errors.length} erros. Verifique o console.`,
          { autoClose: 10000 }
        );
        console.log('Erros na migração:', errors);
      }

      // Mostrar relatório detalhado
      console.log('='.repeat(70));
      console.log('📊 RELATÓRIO DE MIGRAÇÃO AUTOMÁTICA');
      console.log('='.repeat(70));
      console.log(`Total processado: ${allDealsFromPhase.length}`);
      console.log(`✅ Sucessos: ${results.length}`);
      console.log(`❌ Erros: ${errors.length}`);
      console.log('\nDetalhes dos sucessos:', results);
      if (errors.length > 0) {
        console.log('\nDetalhes dos erros:', errors);
      }

    } catch (error) {
      console.error('Erro na migração automática:', error);
      toast.error('Erro na migração automática. Verifique o console.');
    } finally {
      setAutoMigrating(false);
      setMigrationPaused(false);
      setMigrationCancelled(false);
    }
  };

  // Cancelar migração
  const cancelMigration = () => {
    setMigrationCancelled(true);
    setShowMigrationModal(false);
  };

  // Pausar/Continuar migração
  const toggleMigrationPause = () => {
    setMigrationPaused(!migrationPaused);
    if (migrationPaused) {
      toast.info('▶️ Migração continuada');
    } else {
      toast.info('⏸️ Migração pausada');
    }
  };

  // ======= FUNÇÕES PARA MIGRAÇÃO DE COMENTÁRIOS E ANEXOS =======

  // Migrar comentários do negócio antigo para novo (ou criar novo se não especificar)
  const migrateCommentsAndAttachments = async () => {
    if (!commentsConfig.sourceDealId) {
      toast.warning('Preencha o ID do negócio no Bitrix antigo');
      return;
    }

    if (!commentsConfig.targetDealId && !commentsConfig.targetPipeline) {
      toast.warning('Preencha o ID do card no Bitrix novo OU o ID do pipeline');
      return;
    }

    // Se for criar novo deal, targetStage é obrigatório
    if (!commentsConfig.targetDealId && !commentsConfig.targetStage) {
      toast.warning('Preencha o Estágio do pipeline (é obrigatório ao criar novo card)');
      return;
    }

    setCommentsLoading(true);
    setCommentsMigrating(true);
    setCommentsErrors([]);
    setShowCommentsErrors(false);
    setCommentsMigrationResult(null);

    try {
      toast.info('🚀 Iniciando migração de comentários e anexos...');

      // Se não forneceu targetDealId, criar novo deal usando mapeamento
      let targetDealId = commentsConfig.targetDealId;

      if (!targetDealId && commentsConfig.targetPipeline) {
        toast.info('📋 Criando novo card no Bitrix novo...');

        // Usar endpoint de migração completa para criar deal com mapeamento de campos
        // Converter para número apenas se for um número válido, senão enviar como string
        const sourceDealIdNum = /^\d+$/.test(commentsConfig.sourceDealId) 
          ? parseInt(commentsConfig.sourceDealId) 
          : commentsConfig.sourceDealId;
        const targetPipelineNum = /^\d+$/.test(commentsConfig.targetPipeline) 
          ? parseInt(commentsConfig.targetPipeline) 
          : commentsConfig.targetPipeline;
        const targetStageNum = /^\d+$/.test(commentsConfig.targetStage) 
          ? parseInt(commentsConfig.targetStage) 
          : commentsConfig.targetStage;

        const createResponse = await fetch(
          'https://backendconsultabitrix.villelaintegra.com.br/api/bitrix/migrate',
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              dealId: sourceDealIdNum,
              targetPipeline: targetPipelineNum,
              targetStage: targetStageNum
            })
          }
        );

        const createData = await createResponse.json();

        if (!createData.success) {
          throw new Error(createData.message || 'Erro ao criar novo deal');
        }

        targetDealId = createData.newDealId;
        toast.success(`✅ Novo card criado: ${targetDealId}`);
      }

      // Se replaceAll ou migrateFields estiver marcado e targetDealId existir, atualizar todos os campos primeiro
      if ((commentsConfig.replaceAll || commentsConfig.migrateFields) && targetDealId) {
        toast.info('🔄 Atualizando todos os campos do card existente...');
        
        const sourceDealIdNum = /^\d+$/.test(commentsConfig.sourceDealId) 
          ? parseInt(commentsConfig.sourceDealId) 
          : commentsConfig.sourceDealId;
        const targetDealIdNum = /^\d+$/.test(targetDealId) 
          ? parseInt(targetDealId) 
          : targetDealId;

        // Primeiro atualizar todos os campos do card
        const updateResponse = await fetch(
          'https://backendconsultabitrix.villelaintegra.com.br/api/bitrix/migrate/update-deal',
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              sourceDealId: sourceDealIdNum,
              targetDealId: targetDealIdNum
            })
          }
        );

        const updateData = await updateResponse.json();
        
        if (!updateData.success) {
          throw new Error(updateData.message || 'Erro ao atualizar campos do card');
        }
        
        toast.success('✅ Campos do card atualizados com sucesso!');
      }

      // Migrar comentários e arquivos (se selecionado)
      if (!commentsConfig.migrateComments && !commentsConfig.migrateFiles) {
        toast.warning('⚠️ Selecione pelo menos uma opção para migrar (comentários ou arquivos)');
        return;
      }

      // Agora migrar comentários
      // Converter para número apenas se for um número válido, senão enviar como string
      const sourceDealIdNum = /^\d+$/.test(commentsConfig.sourceDealId) 
        ? parseInt(commentsConfig.sourceDealId) 
        : commentsConfig.sourceDealId;
      const targetDealIdNum = /^\d+$/.test(targetDealId) 
        ? parseInt(targetDealId) 
        : targetDealId;

      const response = await fetch(
        'https://backendconsultabitrix.villelaintegra.com.br/api/bitrix/migrate/comments',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            sourceDealId: sourceDealIdNum,
            targetDealId: targetDealIdNum,
            migrateAttachments: commentsConfig.migrateFiles // Usar a opção selecionada
          })
        }
      );

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.message || 'Erro ao migrar comentários');
      }

      setCommentsMigrationResult({
        success: true,
        sourceDealId: commentsConfig.sourceDealId,
        targetDealId: targetDealId,
        commentsCount: data.commentsCount || 0,
        filesCount: data.filesCount || 0,
        message: data.message
      });

      toast.success(
        `✅ Migração concluída!\n📝 ${data.commentsCount || 0} comentário(s)\n📎 ${data.filesCount || 0} arquivo(s)`,
        { autoClose: 5000 }
      );

      console.log('✅ Resultado da migração:', data);
    } catch (error) {
      console.error('❌ Erro na migração:', error);
      
      const errorItem = {
        sourceDealId: commentsConfig.sourceDealId,
        error: error.message,
        timestamp: new Date().toLocaleString('pt-BR')
      };

      setCommentsErrors([errorItem]);
      setShowCommentsErrors(true);

      toast.error(`❌ Erro: ${error.message}`);
    } finally {
      setCommentsLoading(false);
      setCommentsMigrating(false);
    }
  };

  // Alternar expansão do deal
  const toggleDealExpansion = (dealId) => {
    setExpandedDeal(expandedDeal === dealId ? null : dealId);
  };

  // Formatar valor monetário
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  // Formatar data
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-3xl font-bold text-gray-900">Migração de Negócios</h1>
        <p className="text-gray-600 mt-1">Migre negócios do Bitrix antigo para o novo</p>
      </div>

      {/* NOVA SEÇÃO: Migração de Comentários e Anexos */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl shadow-lg p-6 border-2 border-blue-300"
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center">
            <FileText className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-blue-700">Migração de Comentários e Anexos</h2>
            <p className="text-sm text-blue-600">Copie comentários e arquivos de um negócio para outro</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          {/* ID do Negócio Antigo */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              ID do Negócio (Bitrix Antigo) *
            </label>
            <input
              type="text"
              value={commentsConfig.sourceDealId}
              onChange={(e) => setCommentsConfig({ ...commentsConfig, sourceDealId: e.target.value })}
              placeholder="Ex: 1458836 ou ABC123"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={commentsLoading || commentsMigrating}
            />
            <p className="text-xs text-gray-500 mt-1">ID do card no Bitrix antigo (aceita números e letras)</p>
          </div>

          {/* ID do Card Novo */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              ID do Card (Bitrix Novo) [Opcional]
            </label>
            <input
              type="text"
              value={commentsConfig.targetDealId}
              onChange={(e) => setCommentsConfig({ ...commentsConfig, targetDealId: e.target.value })}
              placeholder="Ex: 433890 ou XYZ789"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={commentsLoading || commentsMigrating}
            />
            <p className="text-xs text-gray-500 mt-1">Deixe em branco para criar novo (aceita números e letras)</p>
          </div>

          {/* Pipeline de Destino (se criar novo) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Pipeline Destino [Se criar novo]
            </label>
            <input
              type="text"
              value={commentsConfig.targetPipeline}
              onChange={(e) => setCommentsConfig({ ...commentsConfig, targetPipeline: e.target.value })}
              placeholder="Ex: 7 ou PIPELINE-7"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={commentsLoading || commentsMigrating}
            />
            <p className="text-xs text-gray-500 mt-1">ID da pipeline no Bitrix novo (aceita números e letras)</p>
          </div>

          {/* Stage de Destino (se criar novo) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Stage Destino [Se criar novo]
            </label>
            <input
              type="text"
              value={commentsConfig.targetStage}
              onChange={(e) => setCommentsConfig({ ...commentsConfig, targetStage: e.target.value })}
              placeholder="Ex: 2, 5 ou NEW, PREPARATION"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={commentsLoading || commentsMigrating}
            />
            <p className="text-xs text-gray-500 mt-1">ID do stage (estágio) na pipeline (aceita números e letras)</p>
          </div>
        </div>

        {/* Opções de Migração */}
        <div className="mb-4 space-y-3">
          {/* Opção de Substituir Campos - Só aparece se tiver card de destino */}
          {commentsConfig.targetDealId && (
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={commentsConfig.replaceAll}
                  onChange={(e) => setCommentsConfig({ ...commentsConfig, replaceAll: e.target.checked })}
                  className="mt-1 w-5 h-5 text-amber-600 border-gray-300 rounded focus:ring-amber-500"
                  disabled={commentsLoading || commentsMigrating}
                />
                <div className="flex-1">
                  <p className="text-sm font-medium text-amber-800 mb-1">
                    🔄 Substituir todos os campos do card existente
                  </p>
                  <p className="text-xs text-amber-700">
                    Se marcado, todos os campos do card novo serão atualizados com os dados do card antigo (título, valor, datas, campos personalizados, etc.)
                  </p>
                </div>
              </label>
            </div>
          )}

          {/* Opções de Seleção do que Migrar - Sempre visível */}
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm font-medium text-blue-800 mb-3">
              📋 Selecionar o que migrar:
            </p>
            <div className="space-y-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={commentsConfig.migrateComments}
                  onChange={(e) => setCommentsConfig({ ...commentsConfig, migrateComments: e.target.checked })}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  disabled={commentsLoading || commentsMigrating}
                />
                <span className="text-sm text-blue-700">💬 Migrar comentários</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={commentsConfig.migrateFiles}
                  onChange={(e) => setCommentsConfig({ ...commentsConfig, migrateFiles: e.target.checked })}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  disabled={commentsLoading || commentsMigrating}
                />
                <span className="text-sm text-blue-700">📎 Migrar arquivos dos comentários</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={commentsConfig.migrateFields}
                  onChange={(e) => setCommentsConfig({ ...commentsConfig, migrateFields: e.target.checked })}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  disabled={commentsLoading || commentsMigrating || !commentsConfig.targetDealId}
                />
                <span className="text-sm text-blue-700">
                  📝 Migrar campos {!commentsConfig.targetDealId && '(requer card de destino)'}
                </span>
              </label>
            </div>
          </div>
        </div>

        {/* Info */}
        <div className="p-4 bg-blue-100 border border-blue-300 rounded-lg mb-4">
          <p className="text-sm text-blue-700 font-medium mb-2">ℹ️ Como funciona:</p>
          <ul className="text-xs text-blue-600 space-y-1">
            <li><strong>Cenário 1:</strong> Se você preencher o ID do card novo, os comentários serão migrados para esse card{commentsConfig.replaceAll ? ' e TODOS os campos serão atualizados' : ''}</li>
            <li><strong>Cenário 2:</strong> Se deixar em branco e fornecer a pipeline E o stage, um novo card será criado no Bitrix novo (com o mapeamento de campos) e os comentários serão migrados para ele</li>
            <li><strong>Resultado:</strong> Todos os comentários e anexos do card antigo serão copiados para o card novo{commentsConfig.replaceAll ? ', e todos os campos serão substituídos' : ''}</li>
          </ul>
        </div>

        {/* Botão de Migração */}
        <motion.button
          onClick={migrateCommentsAndAttachments}
          disabled={commentsLoading || commentsMigrating || !commentsConfig.sourceDealId}
          className="w-full px-4 py-3 bg-blue-500 text-white rounded-lg font-medium flex items-center justify-center gap-2 hover:bg-blue-600 disabled:opacity-50 transition-all"
          whileHover={{ scale: commentsLoading || commentsMigrating ? 1 : 1.02 }}
          whileTap={{ scale: commentsLoading || commentsMigrating ? 1 : 0.98 }}
        >
          {commentsLoading || commentsMigrating ? (
            <>
              <RefreshCw className="w-5 h-5 animate-spin" />
              Migrando comentários e anexos...
            </>
          ) : (
            <>
              <Zap className="w-5 h-5" />
              Migrar Comentários e Anexos
            </>
          )}
        </motion.button>

        {/* Resultado da Migração */}
        {commentsMigrationResult && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-4 p-4 bg-green-50 border-2 border-green-300 rounded-lg"
          >
            <div className="flex items-start gap-3">
              <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="text-sm font-bold text-green-700 mb-2">✅ Migração Concluída!</h3>
                <div className="space-y-1 text-sm">
                  <p><span className="font-medium">De:</span> Card #{commentsMigrationResult.sourceDealId}</p>
                  <p><span className="font-medium">Para:</span> Card #{commentsMigrationResult.targetDealId}</p>
                  <p><span className="font-medium">Comentários:</span> {commentsMigrationResult.commentsCount}</p>
                  <p><span className="font-medium">Arquivos:</span> {commentsMigrationResult.filesCount}</p>
                </div>
                <motion.button
                  onClick={() => setCommentsMigrationResult(null)}
                  className="mt-3 px-4 py-2 text-xs bg-green-200 text-green-700 rounded hover:bg-green-300 transition-colors font-medium"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Fazer outra migração
                </motion.button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Erros */}
        {commentsErrors.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-4 p-4 bg-red-50 border-2 border-red-300 rounded-lg"
          >
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-600" />
                <h3 className="text-sm font-bold text-red-700">❌ Erro na Migração</h3>
              </div>
              <motion.button
                onClick={() => setCommentsErrors([])}
                className="px-2 py-1 text-xs bg-red-200 text-red-700 rounded hover:bg-red-300 transition-colors"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                ✕ Fechar
              </motion.button>
            </div>
            
            <div className="space-y-2">
              {commentsErrors.map((error, idx) => (
                <div key={idx} className="p-2 bg-white border border-red-200 rounded">
                  <p className="text-xs text-gray-600"><strong>Negócio ID:</strong> {error.sourceDealId}</p>
                  <p className="text-xs text-red-600 mt-1"><strong>Erro:</strong> {error.error}</p>
                  <p className="text-xs text-gray-500 mt-1"><strong>Data/Hora:</strong> {error.timestamp}</p>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </motion.div>


      {/* NOVA SEÇÃO: Migração Automática de Fase Completa */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl shadow-lg p-6 border-2 border-purple-300"
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
            <Zap className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-purple-700">Migração Automática de Fase</h2>
            <p className="text-sm text-purple-600">Migre uma fase inteira automaticamente (busca TODOS os negócios)</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          {/* Origem */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 bg-red-500 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-xs">A</span>
              </div>
              <h3 className="font-semibold text-gray-900">Origem (Bitrix Antigo)</h3>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Pipeline Origem *
              </label>
              <input
                type="text"
                value={autoMigration.sourcePipeline}
                onChange={(e) => setAutoMigration({ ...autoMigration, sourcePipeline: e.target.value })}
                placeholder="Ex: 80, 41..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                disabled={autoMigrating}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Estágio Origem *
              </label>
              <input
                type="text"
                value={autoMigration.sourceStage}
                onChange={(e) => setAutoMigration({ ...autoMigration, sourceStage: e.target.value })}
                placeholder="Ex: C80:NEW, C41:UC_WAGO4J..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                disabled={autoMigrating}
              />
            </div>
          </div>

          {/* Destino */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-xs">N</span>
              </div>
              <h3 className="font-semibold text-gray-900">Destino (Bitrix Novo)</h3>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Pipeline Destino *
              </label>
              <input
                type="text"
                value={autoMigration.targetPipeline}
                onChange={(e) => setAutoMigration({ ...autoMigration, targetPipeline: e.target.value })}
                placeholder="Ex: 17, 7..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                disabled={autoMigrating}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Estágio Destino *
              </label>
              <input
                type="text"
                value={autoMigration.targetStage}
                onChange={(e) => setAutoMigration({ ...autoMigration, targetStage: e.target.value })}
                placeholder="Ex: C17:NEW, C7:PREPARATION..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                disabled={autoMigrating}
              />
            </div>
          </div>
        </div>

        {/* Indicador de Progresso */}
        {autoMigrating && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="bg-white rounded-lg p-4 mb-4"
          >
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">
                Progresso: {autoMigrationProgress.current} / {autoMigrationProgress.total}
              </span>
              <span className="text-sm font-medium text-purple-600">
                {Math.round((autoMigrationProgress.current / autoMigrationProgress.total) * 100)}%
              </span>
            </div>
            
            <div className="w-full bg-gray-200 rounded-full h-3 mb-3">
              <div
                className="bg-gradient-to-r from-purple-500 to-pink-500 h-3 rounded-full transition-all duration-300"
                style={{ width: `${(autoMigrationProgress.current / autoMigrationProgress.total) * 100}%` }}
              />
            </div>

            <div className="grid grid-cols-3 gap-4 text-center">
              <div className="bg-green-50 rounded-lg p-2">
                <p className="text-xs text-gray-600">Sucessos</p>
                <p className="text-lg font-bold text-green-600">{autoMigrationProgress.success}</p>
              </div>
              <div className="bg-red-50 rounded-lg p-2">
                <p className="text-xs text-gray-600">Erros</p>
                <p className="text-lg font-bold text-red-600">{autoMigrationProgress.errors}</p>
              </div>
              <div className="bg-blue-50 rounded-lg p-2">
                <p className="text-xs text-gray-600">Restantes</p>
                <p className="text-lg font-bold text-blue-600">
                  {autoMigrationProgress.total - autoMigrationProgress.current}
                </p>
              </div>
            </div>

            {autoMigrationProgress.currentDeal && (
              <div className="mt-3 p-2 bg-purple-50 rounded-lg">
                <p className="text-xs text-gray-600">Migrando agora:</p>
                <p className="text-sm font-medium text-purple-700 truncate">
                  {autoMigrationProgress.currentDeal}
                </p>
              </div>
            )}

            {/* Controles de Pausa/Cancelar */}
            <div className="mt-4 flex gap-2 justify-center">
              <motion.button
                onClick={toggleMigrationPause}
                className={`px-4 py-2 rounded-lg font-medium flex items-center gap-2 ${
                  migrationPaused 
                    ? 'bg-green-500 hover:bg-green-600 text-white' 
                    : 'bg-yellow-500 hover:bg-yellow-600 text-white'
                }`}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {migrationPaused ? (
                  <>
                    <Play className="w-4 h-4" />
                    Continuar
                  </>
                ) : (
                  <>
                    <Pause className="w-4 h-4" />
                    Pausar
                  </>
                )}
              </motion.button>

              <motion.button
                onClick={() => setMigrationCancelled(true)}
                className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg font-medium flex items-center gap-2"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <X className="w-4 h-4" />
                Cancelar
              </motion.button>
            </div>
          </motion.div>
        )}

        {/* Botão de Ação */}
        <motion.button
          onClick={migrateEntirePhase}
          disabled={autoMigrating}
          className="w-full px-6 py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-bold text-lg flex items-center justify-center gap-3 hover:from-purple-700 hover:to-pink-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg"
          whileHover={{ scale: autoMigrating ? 1 : 1.02 }}
          whileTap={{ scale: autoMigrating ? 1 : 0.98 }}
        >
          {autoMigrating ? (
            <>
              <Loader2 className="w-6 h-6 animate-spin" />
              Migrando Fase Completa... ({autoMigrationProgress.current}/{autoMigrationProgress.total})
            </>
          ) : (
            <>
              <PlayCircle className="w-6 h-6" />
              Migrar Fase Completa Automaticamente
            </>
          )}
        </motion.button>

        <div className="mt-3 p-3 bg-purple-100 rounded-lg">
          <p className="text-xs text-purple-700">
            💡 <strong>Como funciona:</strong> Busca TODOS os negócios da fase origem (sem limite de 50), 
            depois migra cada um automaticamente para o destino. Pode levar alguns minutos dependendo da quantidade.
          </p>
        </div>
      </motion.div>

      {/* Seções de Origem e Destino (Busca Manual) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        
        {/* BITRIX ANTIGO - ORIGEM */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="bg-gradient-to-br from-red-50 to-orange-50 rounded-xl shadow-lg p-6 border border-red-200"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-red-500 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm">A</span>
            </div>
            <div>
              <h2 className="text-xl font-bold text-red-700">Bitrix ANTIGO</h2>
              <p className="text-sm text-red-600">Origem dos negócios</p>
            </div>
          </div>

          {/* Filtros do Antigo */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Pipeline (ID)
              </label>
              <input
                type="text"
                value={filters.pipeline}
                onChange={(e) => setFilters({ ...filters, pipeline: e.target.value })}
                placeholder="Ex: 80"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Estágio (código Bitrix)
              </label>
              <input
                type="text"
                value={filters.stage}
                onChange={(e) => setFilters({ ...filters, stage: e.target.value })}
                placeholder="Ex: C41:EXECUTING, C41:NEW (não use número)"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                ID do Negócio
              </label>
              <input
                type="text"
                value={filters.dealId}
                onChange={(e) => setFilters({ ...filters, dealId: e.target.value })}
                placeholder="Ex: 1449642"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nome do Responsável
              </label>
              <input
                type="text"
                value={filters.assignedByName}
                onChange={(e) => setFilters({ ...filters, assignedByName: e.target.value })}
                placeholder="Ex: Marcio, ruyther"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              />
            </div>

            <motion.button
              onClick={searchDeals}
              disabled={loading}
              className="w-full px-4 py-3 bg-red-500 text-white rounded-lg font-medium flex items-center justify-center gap-2 hover:bg-red-600 disabled:opacity-50"
              whileHover={{ scale: loading ? 1 : 1.02 }}
              whileTap={{ scale: loading ? 1 : 0.98 }}
            >
              {loading ? (
                <>
                  <RefreshCw className="w-5 h-5 animate-spin" />
                  Buscando...
                </>
              ) : (
                <>
                  <Search className="w-5 h-5" />
                  Buscar Negócios
                </>
              )}
            </motion.button>
          </div>
        </motion.div>

        {/* BITRIX NOVO - DESTINO */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl shadow-lg p-6 border border-green-200"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm">N</span>
            </div>
            <div>
              <h2 className="text-xl font-bold text-green-700">Bitrix NOVO</h2>
              <p className="text-sm text-green-600">Destino da migração</p>
            </div>
          </div>

          {/* Configuração do Destino */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Pipeline de Destino (ID)
              </label>
              <input
                type="text"
                value={migrationConfig.targetPipeline}
                onChange={(e) => setMigrationConfig({ ...migrationConfig, targetPipeline: e.target.value })}
                placeholder="Ex: 0, 1, 2..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Estágio de Destino (ID)
              </label>
              <input
                type="text"
                value={migrationConfig.targetStage}
                onChange={(e) => setMigrationConfig({ ...migrationConfig, targetStage: e.target.value })}
                placeholder="Ex: NEW, PREPARATION, PREPAYMENT_INVOICE..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Responsável (ID - Opcional)
              </label>
              <input
                type="text"
                value={migrationConfig.targetUserId}
                onChange={(e) => setMigrationConfig({ ...migrationConfig, targetUserId: e.target.value })}
                placeholder="Ex: 1, 2, 3... (deixe vazio para manter atual)"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                ADV Responsável (ID - Opcional)
              </label>
              <input
                type="text"
                value={migrationConfig.targetAdvID}
                onChange={(e) => setMigrationConfig({ ...migrationConfig, targetAdvID: e.target.value })}
                placeholder="Ex: 1, 2, 3... (deixe vazio para manter atual)"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <p className="text-sm text-green-700">
                <strong>Configuração:</strong> {migrationConfig.targetPipeline && migrationConfig.targetStage 
                  ? `Pipeline ${migrationConfig.targetPipeline} - Estágio ${migrationConfig.targetStage}`
                  : 'Digite pipeline e estágio de destino'
                }
              </p>
              <p className="text-xs text-green-600 mt-1">
                💡 O sistema irá replicar TODOS os dados: empresa, contato, lead, documentos, arquivos
              </p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Lista de Negócios */}
      {deals.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden"
        >
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <h2 className="text-xl font-semibold text-gray-900">
                  Negócios Encontrados ({deals.length})
                </h2>
                {selectedDeals.length > 0 && (
                  <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                    {selectedDeals.length} selecionado(s)
                  </span>
                )}
              </div>
              
              <div className="flex gap-3">
                <motion.button
                  onClick={toggleSelectAll}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-2"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {selectAll ? (
                    <>
                      <CheckSquare className="w-4 h-4" />
                      Desselecionar Todos
                    </>
                  ) : (
                    <>
                      <Square className="w-4 h-4" />
                      Selecionar Todos
                    </>
                  )}
                </motion.button>

                {selectedDeals.length > 0 && (
                  <motion.button
                    onClick={migrateSelectedDeals}
                    disabled={migrating || !migrationConfig.targetPipeline || !migrationConfig.targetStage}
                    className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-medium flex items-center gap-2 hover:from-purple-700 hover:to-pink-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    whileHover={{ scale: migrating ? 1 : 1.02 }}
                    whileTap={{ scale: migrating ? 1 : 0.98 }}
                  >
                    {migrating ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        Migrando...
                      </>
                    ) : (
                      <>
                        <ArrowRightLeft className="w-4 h-4" />
                        Migrar {selectedDeals.length} Selecionado(s)
                      </>
                    )}
                  </motion.button>
                )}
                
                <motion.button
                  onClick={exportToCSV}
                  className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors flex items-center gap-2"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Download className="w-4 h-4" />
                  Exportar CSV
                </motion.button>
              </div>
            </div>
          </div>

          <div className="divide-y divide-gray-200">
            {deals.map((deal, index) => (
              <motion.div
                key={deal.ID}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
                className={`p-6 transition-colors ${selectedDeals.includes(deal.ID) ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
              >
                <div className="flex items-start gap-4">
                  {/* Checkbox de Seleção */}
                  <div className="flex items-center pt-1">
                    <input
                      type="checkbox"
                      checked={selectedDeals.includes(deal.ID)}
                      onChange={() => toggleDealSelection(deal.ID)}
                      className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                    />
                  </div>

                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">{deal.TITLE}</h3>
                      <span className="px-3 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
                        ID: {deal.ID}
                      </span>
                      {selectedDeals.includes(deal.ID) && (
                        <CheckCircle className="w-5 h-5 text-blue-600" />
                      )}
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                      <div>
                        <p className="text-xs text-gray-500">Pipeline</p>
                        <p className="text-sm font-medium text-gray-900">{deal.CATEGORY_ID || '-'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Estágio</p>
                        <p className="text-sm font-medium text-gray-900">{deal.STAGE_ID || '-'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Valor</p>
                        <p className="text-sm font-medium text-green-600">{formatCurrency(deal.OPPORTUNITY)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Data Criação</p>
                        <p className="text-sm font-medium text-gray-900">{formatDate(deal.DATE_CREATE)}</p>
                      </div>
                    </div>

                    {expandedDeal === deal.ID && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="mt-4 pt-4 border-t border-gray-200"
                      >
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                          <div>
                            <p className="text-xs text-gray-500">Responsável</p>
                            <p className="text-sm font-medium text-gray-900">ID: {deal.ASSIGNED_BY_ID || '-'}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">Moeda</p>
                            <p className="text-sm font-medium text-gray-900">{deal.CURRENCY_ID || '-'}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">Última Modificação</p>
                            <p className="text-sm font-medium text-gray-900">{formatDate(deal.DATE_MODIFY)}</p>
                          </div>
                          {deal.UF_CRM_1561394928 && (
                            <div>
                              <p className="text-xs text-gray-500">PC</p>
                              <p className="text-sm font-medium text-gray-900">{deal.UF_CRM_1561394928}</p>
                            </div>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </div>

                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => toggleDealExpansion(deal.ID)}
                      className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                    >
                      {expandedDeal === deal.ID ? (
                        <ChevronUp className="w-5 h-5 text-gray-600" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-600" />
                      )}
                    </button>

                    <motion.button
                      onClick={() => migrateSingleDeal(deal.ID)}
                      disabled={migrating || !migrationConfig.targetPipeline || !migrationConfig.targetStage}
                      className="px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg font-medium flex items-center gap-2 hover:from-purple-600 hover:to-pink-600 disabled:opacity-50 disabled:cursor-not-allowed"
                      whileHover={{ scale: migrating ? 1 : 1.02 }}
                      whileTap={{ scale: migrating ? 1 : 0.98 }}
                    >
                      {migrating ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <ArrowRightLeft className="w-4 h-4" />
                      )}
                      Migrar
                    </motion.button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Estatísticas */}
      {deals.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl shadow-lg p-6 border border-gray-200"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total de Negócios</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{stats.total}</p>
              </div>
              <Briefcase className="w-10 h-10 text-blue-500" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-xl shadow-lg p-6 border border-gray-200"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Valor Total</p>
                <p className="text-2xl font-bold text-green-600 mt-1">{formatCurrency(stats.totalValue)}</p>
              </div>
              <DollarSign className="w-10 h-10 text-green-500" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-xl shadow-lg p-6 border border-gray-200"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Valor Médio</p>
                <p className="text-2xl font-bold text-purple-600 mt-1">{formatCurrency(stats.avgValue)}</p>
              </div>
              <DollarSign className="w-10 h-10 text-purple-500" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-xl shadow-lg p-6 border border-gray-200"
          >
            <div>
              <p className="text-sm text-gray-600 mb-2">Por Pipeline</p>
              <div className="space-y-1">
                {Object.entries(stats.byPipeline).map(([pipeline, count]) => (
                  <div key={pipeline} className="flex justify-between text-sm">
                    <span className="text-gray-700">Pipeline {pipeline}:</span>
                    <span className="font-semibold text-gray-900">{count}</span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Estado vazio */}
      {!loading && deals.length === 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="bg-white rounded-xl shadow-lg p-12 text-center border border-gray-200"
        >
          <Search className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Nenhum resultado ainda</h3>
          <p className="text-gray-600">Configure os filtros acima e clique em "Buscar Negócios"</p>
        </motion.div>
      )}

      {/* Modal de Confirmação de Migração */}
      {showMigrationModal && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6"
          >
            {/* Header */}
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
                <ArrowRightLeft className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">Confirmar Migração Automática</h3>
                <p className="text-sm text-gray-600">Migração completa de fase</p>
              </div>
            </div>

            {/* Detalhes da Migração */}
            <div className="bg-gray-50 rounded-xl p-4 mb-6">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-600 font-medium">Origem:</p>
                  <p className="text-gray-900">Pipeline {autoMigration.sourcePipeline}</p>
                  <p className="text-gray-700 text-xs">{autoMigration.sourceStage}</p>
                </div>
                <div>
                  <p className="text-gray-600 font-medium">Destino:</p>
                  <p className="text-gray-900">Pipeline {autoMigration.targetPipeline}</p>
                  <p className="text-gray-700 text-xs">{autoMigration.targetStage}</p>
                </div>
              </div>
            </div>

            {/* Aviso */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-6">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-yellow-800">Atenção!</p>
                  <p className="text-sm text-yellow-700 mt-1">
                    Esta operação irá migrar <strong>TODOS</strong> os negócios da fase selecionada. 
                    Isso pode levar vários minutos dependendo da quantidade de negócios.
                  </p>
                </div>
              </div>
            </div>

            {/* Botões */}
            <div className="flex gap-3">
              <motion.button
                onClick={() => setShowMigrationModal(false)}
                className="flex-1 px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium transition-colors"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Cancelar
              </motion.button>
              
              <motion.button
                onClick={confirmMigration}
                className="flex-1 px-4 py-3 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white rounded-xl font-medium flex items-center justify-center gap-2 transition-all"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <ArrowRightLeft className="w-4 h-4" />
                Iniciar Migração
              </motion.button>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Modal de Confirmação - Migração de Comentários */}
    </div>
  );
};

export default MigrationPage;

